import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Plus, Sparkles, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { getOrganizationMembers } from "../../api/organizationAccessApi";
import { getToken, useMsalAuth } from "../../authConfig";
import {
    createDocumentationDefinition,
    listDocumentationDefinitions,
    publishDocumentationDefinition,
    testDocumentationDefinition,
    updateDocumentationDefinition,
    validateDocumentationDefinition
} from "./definitionApi";
import {
    DocumentationApiError,
    DocumentationDefinitionCreateInput,
    DocumentationDefinitionVersion,
    DocumentationFieldDefinition,
    DocumentationFieldType,
    DocumentationSourcePolicy
} from "./models";
import styles from "./DocumentationDefinitionAdmin.module.css";

type Props = { organizationId: string };
type Scope = { type: "Organization" | "Business Area"; id: string };
type NormalizeMode = "create" | "update";

function field(
    fieldKey: string,
    label: { sv: string; en: string },
    type: DocumentationFieldType,
    sourcePolicy: DocumentationSourcePolicy,
    required: boolean,
    enumValues: string[] = []
): DocumentationFieldDefinition {
    const hasTextLength = type === "text" || type === "string";
    return {
        field_key: fieldKey,
        label,
        description: "",
        type,
        required,
        nullable: !required,
        source_policy: sourcePolicy,
        employee_editable: true,
        render_when_empty: false,
        constraints: {
            min_length: required && hasTextLength ? 1 : null,
            max_length: hasTextLength ? 4000 : null,
            enum_values: enumValues
        }
    };
}

function normalizedName(value: string): string {
    return value
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase()
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ");
}

function stableHash(value: string): string {
    let hash = 2166136261;
    for (const character of value) {
        hash ^= character.codePointAt(0) || 0;
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
}

function customDefinitionKey(name: string): string {
    const readable = normalizedName(name)
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 48);
    return readable ? `custom_${readable}` : `custom_${stableHash(name)}`;
}

function definitionFromName(
    organizationId: string,
    scope: Scope,
    name: string,
    hasPublishedDefault: boolean
): DocumentationDefinitionVersion {
    const now = new Date().toISOString();
    const definition: DocumentationDefinitionVersion = {
        id: `new_${crypto.randomUUID()}`,
        type: "documentation_definition_version",
        schema_version: 1,
        organization_id: organizationId,
        scope_type: scope.type,
        scope_id: scope.id,
        definition_key: customDefinitionKey(name),
        version: 1,
        revision: 1,
        status: "draft",
        display_name: { sv: name, en: name },
        description: { sv: "", en: "" },
        classification: {
            instructions: `Select this documentation type when the employee input matches the ordinary professional meaning of “${name}”.`,
            positive_examples: [],
            negative_examples: []
        },
        behaviors: {
            is_default_type: !hasPublishedDefault,
            requires_active_goal: false,
            creates_deviation_case: false,
            requires_ibic: false,
            follow_up_workflow_id: null
        },
        context_selectors: [],
        fields: [field("content", { sv: "Innehåll", en: "Content" }, "text", "ai_from_employee_input", true)],
        ai_steps: [],
        validation_rules: [],
        render_template: { title: { sv: "{display_name}", en: "{display_name}" }, sections: [] },
        prompt_version: "documentation-v1",
        created_at: now,
        created_by: "",
        updated_at: now,
        updated_by: ""
    };

    definition.classification.positive_examples = [`The employee explicitly records this event as “${name}” documentation.`];
    definition.classification.negative_examples = [`This input belongs to a different documentation purpose and must not be classified as “${name}”.`];

    return normalizeDefinition(definition, "create");
}

function normalizeDefinition(definition: DocumentationDefinitionVersion, mode: NormalizeMode): DocumentationDefinitionVersion {
    const fields = (definition.fields || []).map(item => ({
        ...item,
        nullable: !item.required,
        constraints: {
            ...item.constraints,
            enum_values: item.type === "enum" ? item.constraints.enum_values : []
        }
    }));
    const selectors = definition.context_selectors || [];
    const normalized: DocumentationDefinitionVersion = {
        ...definition,
        fields,
        context_selectors: definition.behaviors.requires_active_goal && !selectors.includes("goals.active") ? [...selectors, "goals.active"] : selectors
    };
    // Only a newly created type is seeded with defaults. An existing definition keeps the
    // AI steps, validation rules and render template it was configured with.
    if (mode === "update") return normalized;

    const usesContext = fields.some(item => item.source_policy === "ai_with_client_context");
    const outputFields = fields.filter(item => item.source_policy !== "employee_input").map(item => item.field_key);
    const activeGoalRules = definition.behaviors.requires_active_goal
        ? fields
              .filter(item => item.type === "goal_reference")
              .map(item => ({
                  rule_key: `active_${item.field_key}`,
                  type: "active_goal",
                  severity: "error",
                  field_key: item.field_key,
                  other_field_key: null,
                  conditions: [],
                  value: null,
                  message: { sv: "Välj ett aktivt mål.", en: "Select an active goal." }
              }))
        : [];
    return {
        ...normalized,
        ai_steps: [
            {
                step_key: "generate_documentation_fields",
                type: usesContext ? "generate_fields" : "extract_fields",
                instructions: "Populate the configured fields without inventing facts. Use only the employee input and explicitly selected context.",
                input_fields: ["raw_input"],
                context_selectors: usesContext ? normalized.context_selectors : [],
                output_fields: outputFields
            }
        ],
        validation_rules: activeGoalRules,
        render_template: {
            title: { sv: "{display_name}", en: "{display_name}" },
            sections: [
                {
                    section_key: "documentation",
                    label: { sv: definition.display_name.sv || definition.definition_key, en: definition.display_name.en || definition.definition_key },
                    field_keys: fields.map(item => item.field_key)
                }
            ]
        }
    };
}

function createInput(definition: DocumentationDefinitionVersion): DocumentationDefinitionCreateInput {
    return {
        scope_type: definition.scope_type,
        scope_id: definition.scope_id,
        definition_key: definition.definition_key,
        display_name: definition.display_name,
        description: definition.description,
        classification: definition.classification,
        behaviors: definition.behaviors,
        context_selectors: definition.context_selectors,
        fields: definition.fields,
        ai_steps: definition.ai_steps,
        validation_rules: definition.validation_rules,
        render_template: definition.render_template
    };
}

function syntheticInputFor(name: string): string {
    return `The employee explicitly records this event as “${name}” documentation.`;
}

function syntheticTestData(
    definition: DocumentationDefinitionVersion,
    sampleInput: string
): { context: Record<string, unknown>; fields: Record<string, unknown> } {
    const goal = {
        goal_id: "synthetic_goal",
        project_id: "synthetic_project",
        title: "Synthetic active goal",
        description: "Used only by the Definition test.",
        status: "active",
        version: 1
    };
    const context = Object.fromEntries(
        definition.context_selectors.map(selector => [
            selector,
            selector === "goals.active" ? [goal] : selector.startsWith("notes.") ? [] : "Synthetic context"
        ])
    );
    const valueFor = (item: DocumentationFieldDefinition): unknown => {
        if (item.type === "integer" || item.type === "number") return 1;
        if (item.type === "boolean") return true;
        if (item.type === "date") return new Date().toISOString().slice(0, 10);
        if (item.type === "datetime") return new Date().toISOString();
        if (item.type === "enum") return item.constraints.enum_values[0] || "synthetic";
        if (item.type === "string_list") return [sampleInput];
        if (item.type === "goal_reference") return goal.goal_id;
        if (item.type === "client_context_reference") return definition.context_selectors[0] || "synthetic_context";
        return sampleInput;
    };
    const fields = Object.fromEntries(definition.fields.filter(item => item.source_policy === "employee_input").map(item => [item.field_key, valueFor(item)]));
    return { context, fields };
}

function errorText(error: unknown): string {
    if (!(error instanceof DocumentationApiError)) return error instanceof Error ? error.message : String(error);
    const issues = error.details?.issues;
    if (!Array.isArray(issues)) return error.message;
    const messages = issues
        .filter(item => typeof item === "object" && item !== null && "message" in item)
        .map(item => String((item as { message: unknown }).message));
    return messages.join(" ") || error.message;
}

function definitionName(definition: DocumentationDefinitionVersion, language: string): string {
    return definition.display_name[language] || definition.display_name.en || definition.display_name.sv || definition.definition_key;
}

export function DocumentationDefinitionAdmin({ organizationId }: Props) {
    const { t, i18n } = useTranslation();
    const client = useMsalAuth ? useMsal().instance : undefined;
    const [definitions, setDefinitions] = useState<DocumentationDefinitionVersion[]>([]);
    const [scopes, setScopes] = useState<Array<Scope & { name: string }>>([
        { type: "Organization", id: organizationId, name: t("settingsPage.company.documentationDefinitions.organizationScope") }
    ]);
    const [scope, setScope] = useState<Scope>({ type: "Organization", id: organizationId });
    const [newName, setNewName] = useState("");
    const [adding, setAdding] = useState(false);
    const [showAdd, setShowAdd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const token = useCallback(async () => (client ? getToken(client) : undefined), [client]);
    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const loaded = await listDocumentationDefinitions(scope.type, scope.id, await token());
            setDefinitions(loaded);
            return loaded;
        } catch (loadError) {
            setError(errorText(loadError));
            return [];
        } finally {
            setLoading(false);
        }
    }, [scope.id, scope.type, token]);

    useEffect(() => {
        void reload();
    }, [reload]);

    useEffect(() => {
        const loadScopes = async () => {
            try {
                const directory = await getOrganizationMembers(organizationId, await token());
                setScopes([
                    {
                        type: "Organization",
                        id: organizationId,
                        name: directory.organization.name || t("settingsPage.company.documentationDefinitions.organizationScope")
                    },
                    ...directory.visibleScopes
                        .filter(item => item.scopeType === "Business Area")
                        .map(item => ({ type: "Business Area" as const, id: item.id, name: item.name }))
                ]);
            } catch {
                // Definition endpoints still enforce scope access.
            }
        };
        void loadScopes();
    }, [organizationId, t, token]);

    const publishedDefinitions = useMemo(
        () =>
            definitions
                .filter(item => item.status === "published")
                .sort((left, right) => definitionName(left, i18n.language).localeCompare(definitionName(right, i18n.language), i18n.language)),
        [definitions, i18n.language]
    );

    const addDefinition = async (event: FormEvent) => {
        event.preventDefault();
        const name = newName.trim();
        if (!name || adding) return;
        setAdding(true);
        setError(null);
        setNotice(null);
        try {
            const duplicate = publishedDefinitions.some(item => normalizedName(definitionName(item, i18n.language)) === normalizedName(name));
            if (duplicate) throw new Error(t("settingsPage.company.documentationDefinitions.simpleDuplicate"));

            // publishedDefinitions is scope-filtered, so a fresh Business Area looks
            // empty even when the organization already has a default. Only an
            // Organization-scoped type may claim the default slot; a Business-Area type
            // inherits the organization default instead of creating a second one.
            const hasPublishedDefault = publishedDefinitions.some(item => item.behaviors.is_default_type) || scope.type !== "Organization";
            const desired = definitionFromName(organizationId, scope, name, hasPublishedDefault);
            const existingDraft = definitions.find(
                item =>
                    item.status === "draft" &&
                    (item.definition_key === desired.definition_key || normalizedName(definitionName(item, i18n.language)) === normalizedName(name))
            );
            const idToken = await token();
            const saved = existingDraft
                ? await updateDocumentationDefinition(
                      normalizeDefinition(
                          {
                              // The existing draft keeps its configuration; only the typed name and
                              // the default-type flag are refreshed.
                              ...existingDraft,
                              display_name: desired.display_name,
                              classification: existingDraft.classification?.instructions ? existingDraft.classification : desired.classification,
                              behaviors: { ...existingDraft.behaviors, is_default_type: desired.behaviors.is_default_type },
                              fields: existingDraft.fields?.length ? existingDraft.fields : desired.fields,
                              ai_steps: existingDraft.ai_steps?.length ? existingDraft.ai_steps : desired.ai_steps,
                              validation_rules: existingDraft.validation_rules || desired.validation_rules,
                              render_template: existingDraft.render_template || desired.render_template
                          },
                          "update"
                      ),
                      idToken
                  )
                : await createDocumentationDefinition(createInput(desired), `auto-${scope.type}-${scope.id}-${desired.definition_key}`, idToken);

            const validation = await validateDocumentationDefinition(saved.id, idToken);
            if (!validation.valid) {
                throw new Error(validation.issues.map(issue => issue.message).join(" ") || t("settingsPage.company.documentationDefinitions.validationFailed"));
            }

            const sampleInput = syntheticInputFor(name);
            const synthetic = syntheticTestData(saved, sampleInput);
            const testResult = await testDocumentationDefinition(saved, sampleInput, i18n.language, synthetic.context, synthetic.fields, idToken);
            if (!testResult.success || !testResult.definition) {
                throw new Error(t("settingsPage.company.documentationDefinitions.simpleTestFailed"));
            }

            await publishDocumentationDefinition(testResult.definition, idToken);
            await reload();
            setNewName("");
            setShowAdd(false);
            setNotice(t("settingsPage.company.documentationDefinitions.simpleAdded", { name }));
        } catch (addError) {
            setError(errorText(addError));
        } finally {
            setAdding(false);
        }
    };

    return (
        <section className={styles.root}>
            <div className={styles.heading}>
                <div>
                    <h4>{t("settingsPage.company.documentationDefinitions.title")}</h4>
                    <p>{t("settingsPage.company.documentationDefinitions.simpleDescription")}</p>
                </div>
            </div>

            {scopes.length > 1 ? (
                <label className={styles.scopePicker}>
                    {t("settingsPage.company.documentationDefinitions.scope")}
                    <select
                        value={`${scope.type}:${scope.id}`}
                        onChange={event => {
                            const selected = scopes.find(item => `${item.type}:${item.id}` === event.target.value);
                            if (!selected) return;
                            setScope({ type: selected.type, id: selected.id });
                            setShowAdd(false);
                            setNewName("");
                            setNotice(null);
                        }}
                    >
                        {scopes.map(item => (
                            <option value={`${item.type}:${item.id}`} key={`${item.type}:${item.id}`}>
                                {item.name}
                            </option>
                        ))}
                    </select>
                </label>
            ) : null}

            {error ? <div className={styles.error}>{error}</div> : null}
            {notice ? <div className={styles.notice}>{notice}</div> : null}

            <div className={styles.listHeader}>
                <h5>{t("settingsPage.company.documentationDefinitions.simpleListTitle")}</h5>
                {!showAdd ? (
                    <button type="button" onClick={() => setShowAdd(true)} disabled={adding}>
                        <Plus size={16} /> {t("settingsPage.company.documentationDefinitions.simpleAdd")}
                    </button>
                ) : null}
            </div>

            {showAdd ? (
                <form className={styles.addForm} onSubmit={addDefinition}>
                    <label>
                        {t("settingsPage.company.documentationDefinitions.simpleName")}
                        <input
                            autoFocus
                            value={newName}
                            onChange={event => setNewName(event.target.value)}
                            placeholder={t("settingsPage.company.documentationDefinitions.simpleNamePlaceholder")}
                            disabled={adding}
                        />
                    </label>
                    <div className={styles.addActions}>
                        <button type="submit" className={styles.primaryAction} disabled={adding || !newName.trim()}>
                            <Sparkles size={16} />
                            {adding
                                ? t("settingsPage.company.documentationDefinitions.simpleAdding")
                                : t("settingsPage.company.documentationDefinitions.simpleAddAndEnable")}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowAdd(false);
                                setNewName("");
                                setError(null);
                            }}
                            disabled={adding}
                        >
                            <X size={16} /> {t("settingsPage.company.documentationDefinitions.simpleCancel")}
                        </button>
                    </div>
                </form>
            ) : null}

            {loading ? <p className={styles.loading}>{t("settingsPage.company.documentationDefinitions.loading")}</p> : null}
            {!loading && publishedDefinitions.length === 0 ? (
                <p className={styles.empty}>{t("settingsPage.company.documentationDefinitions.simpleEmpty")}</p>
            ) : null}
            {publishedDefinitions.length > 0 ? (
                <ul className={styles.nameList}>
                    {publishedDefinitions.map(definition => (
                        <li key={definition.id}>
                            <span>{definitionName(definition, i18n.language)}</span>
                        </li>
                    ))}
                </ul>
            ) : null}
        </section>
    );
}
